import { createContext, useContext } from "react";

import ArrowCircle from "@/components/ArrowCircle";
import Box from "@/components/Box";
import useBreakPointBasedProp from "@/hooks/useBreakPointBasedProp";
import usePrevious from "@/hooks/usePrevious";
import { PageButton } from "@/v2/Buttons";

const PaginationContext = createContext();

function MobilePagination() {
  const { totalPages, currentPage, hasNextPage, hasPreviousPage, size, onChange } =
    useContext(PaginationContext);

  return (
    <>
      {hasPreviousPage && (
        <ArrowCircle direction="left" cursor="pointer" onClick={() => onChange(currentPage - 1)} />
      )}
      {hasPreviousPage && currentPage > 2 && (
        <PageButton
          data-component-name="PaginationButton"
          label="..."
          bg="haze"
          color="dark"
          width={size}
        />
      )}
      <PageButton
        data-component-name="PaginationButton"
        key={currentPage}
        label={currentPage}
        bg="dark"
        color="white"
        width={size}
      />
      {hasNextPage && currentPage < totalPages - 1 && (
        <PageButton
          data-component-name="PaginationButton"
          label="..."
          bg="haze"
          color="dark"
          width={size}
        />
      )}
      {hasNextPage && <ArrowCircle cursor="pointer" onClick={() => onChange(currentPage + 1)} />}
    </>
  );
}

function defaultGetPageButtonProps(pageNumber, currentPage) {
  if (currentPage === pageNumber) {
    return { bg: "dark", color: "white", disabled: false };
  }
  return { bg: "haze", color: "dark", disabled: false };
}

function DefaultPagination() {
  const {
    totalPages,
    currentPage,
    hasNextPage,
    hasPreviousPage,
    size,
    onChange,
    siblingCount,
    getPageButtonProps: getPageButtonPropsFn,
    showArrows
  } = useContext(PaginationContext);

  const getPageButtonProps = getPageButtonPropsFn || defaultGetPageButtonProps;

  // Generate array of page numbers to show around current page
  const middlePages = Array.from(
    { length: siblingCount * 2 + 1 },
    (_, i) => currentPage - siblingCount + i
  ).filter(page => page > 0 && page <= totalPages);

  // Determine if we need ellipsis
  const showLeftEllipsis = middlePages.length > 0 && middlePages[0] > 2;
  const showRightEllipsis =
    middlePages.length > 0 && middlePages[middlePages.length - 1] < totalPages - 1;

  const firstPageProps = getPageButtonProps(1, currentPage);
  const lastPageProps = getPageButtonProps(totalPages, currentPage);
  const prevPageProps = getPageButtonProps(currentPage - 1, currentPage);
  const nextPageProps = getPageButtonProps(currentPage + 1, currentPage);

  return (
    <>
      {showArrows && hasPreviousPage && (
        <ArrowCircle
          direction="left"
          cursor={prevPageProps.disabled ? "not-allowed" : "pointer"}
          onClick={() => !prevPageProps.disabled && onChange(currentPage - 1)}
          opacity={prevPageProps.disabled ? 0.5 : 1}
        />
      )}
      {!middlePages.includes(1) && (
        <PageButton
          data-component-name="PaginationButton"
          label="1"
          bg={firstPageProps.bg}
          color={firstPageProps.color}
          disabled={firstPageProps.disabled}
          onClick={() => !firstPageProps.disabled && onChange(1)}
          width={size}
        />
      )}
      {showLeftEllipsis && (
        <PageButton
          data-component-name="PaginationButton"
          label="..."
          bg="haze"
          color="dark"
          width={size}
        />
      )}
      {middlePages.map(pageNumber => {
        const pageProps = getPageButtonProps(pageNumber, currentPage);
        return (
          <PageButton
            key={pageNumber}
            label={pageNumber}
            bg={pageProps.bg}
            color={pageProps.color}
            disabled={pageProps.disabled}
            onClick={() => !pageProps.disabled && onChange(pageNumber)}
            width={size}
            data-component-name="PaginationButton"
          />
        );
      })}
      {showRightEllipsis && (
        <PageButton
          data-component-name="PaginationButton"
          label="..."
          bg="haze"
          color="dark"
          width={size}
        />
      )}
      {!middlePages.includes(totalPages) && (
        <PageButton
          label={totalPages}
          bg={lastPageProps.bg}
          color={lastPageProps.color}
          disabled={lastPageProps.disabled}
          onClick={() => !lastPageProps.disabled && onChange(totalPages)}
          width={size}
          data-component-name="PaginationButton"
        />
      )}
      {showArrows && hasNextPage && (
        <ArrowCircle
          cursor={nextPageProps.disabled ? "not-allowed" : "pointer"}
          onClick={() => !nextPageProps.disabled && onChange(currentPage + 1)}
          opacity={nextPageProps.disabled ? 0.5 : 1}
        />
      )}
    </>
  );
}

function Pagination() {
  const { variant: variantProp } = useContext(PaginationContext);

  const variant = useBreakPointBasedProp(variantProp) || "default";

  if (variant === "mobile") {
    return <MobilePagination />;
  }

  if (variant === "default") {
    return <DefaultPagination />;
  }
}

function PaginationWrapper({
  // totalPages,
  // currentPage,
  // hasNextPage,
  // hasPreviousPage,
  pageInfo: newPageInfo,
  onChange,
  size,
  variant,
  siblingCount: _siblingCount,
  showArrows: _showArrows,
  getPageButtonProps
}) {
  const siblingCount = useBreakPointBasedProp(_siblingCount);
  const showArrows = useBreakPointBasedProp(_showArrows);
  const previousPageInfo = usePrevious(newPageInfo);
  const pageInfo = newPageInfo || previousPageInfo;

  if (pageInfo === undefined) return null;

  if (!pageInfo.totalPages || pageInfo.currentPage === undefined || pageInfo.totalPages === 1)
    return null;

  return (
    <Box display="flex" justifyContent="center" alignItems="center" gap={[10, 10, 20]}>
      <PaginationContext.Provider
        value={{
          onChange,
          size,
          ...pageInfo,
          variant,
          siblingCount,
          showArrows,
          getPageButtonProps
        }}
      >
        <Pagination />
      </PaginationContext.Provider>
    </Box>
  );
}

PaginationWrapper.defaultProps = {
  variant: "default",
  siblingCount: 1
};

export default PaginationWrapper;
